import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import SetupPanel from './SetupPanel';
import { powerMethodLabel } from './powerMethods';
import AndroidPowerPolicyDiagnostics from './AndroidPowerPolicyDiagnostics';
export { powerPolicyValue } from './AndroidPowerPolicyDiagnostics';

const STARTUP_STATES = [
  ['awake', 'Awake and ready'], ['available', 'Available when needed'], ['unchanged', 'Leave unchanged'],
];
const SHUTDOWN_STATES = [
  ['standby', 'Standby / asleep'], ['off', 'Fully off'], ['stop_playback', 'Stop playback but stay awake'],
  ['remain_awake', 'Remain awake'], ['unchanged', 'Leave unchanged'],
];
const STRATEGIES = [
  ['direct_method', 'Send a direct command'], ['follows_device', 'Follows another device'],
  ['always_on', 'No action — remains awake'], ['manual', 'Manual action'], ['unavailable', 'Not configured — leave unchanged'],
];
const TRIGGERS = {
  startup: [['power', 'when it powers on'], ['input_selection', 'when its input is selected'], ['home', 'when Home is sent'], ['wake', 'when it wakes']],
  shutdown: [['standby', 'when it enters standby'], ['power', 'when it turns off']],
};
const CEC_EFFECTS = {
  startup: [['target_woke', 'Observed device woke'], ['tv_woke', 'TV woke'], ['input_selected', 'Correct TV input selected']],
  shutdown: [['target_slept', 'Observed device slept'], ['tv_turned_off', 'TV turned off'], ['playback_stopped', 'Playback stopped']],
};

const defaultPhase = (phase) => ({
  desiredState: phase === 'startup' ? 'awake' : 'standby',
  strategy: 'unavailable',
  methodId: null,
  dependsOnDeviceId: null,
  trigger: phase === 'startup' ? 'wake' : 'standby',
  notes: '',
});

const parseObject = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return {}; }
};

export function lifecycleDevices(screen, devices) {
  const byId = new Map(devices.map((device) => [Number(device.id), device]));
  const result = [];
  const add = (id, role, fallbackName) => {
    const deviceId = Number(id);
    if (!deviceId || result.some((entry) => entry.id === deviceId)) return;
    const source = byId.get(deviceId);
    const integration = parseObject(source?.integration_data || source?.integrationData);
    const entry = { ...source, id: deviceId, role, name: source?.name || fallbackName || `Device ${deviceId}` };
    if (integration.appNode?.powerPolicy) entry.powerPolicy = integration.appNode.powerPolicy;
    result.push(entry);
  };
  add(screen?.televisionDeviceId, 'television', screen?.televisionName);
  (screen?.players || []).forEach((player) => add(
    player.deviceId || player.id,
    Number(player.deviceId || player.id) === Number(screen?.preferredPlayerDeviceId) ? 'preferred player' : 'player',
    player.name
  ));
  return result;
}

export function lifecycleSummary(lifecycle, phase, devicesById = new Map(), methodsById = new Map()) {
  const config = lifecycle?.[phase] || defaultPhase(phase);
  if (config.strategy === 'direct_method') {
    return config.methodId
      ? `Suitestream sends ${methodsById.get(config.methodId)?.label || 'the selected direct method'}.`
      : 'Choose a direct method for this device.';
  }
  if (config.strategy === 'follows_device') {
    const dependency = devicesById.get(Number(config.dependsOnDeviceId));
    const action = config.trigger === 'input_selection' ? 'its input is selected'
      : config.trigger === 'home' ? 'Home is sent'
        : config.trigger === 'power' ? 'it powers on or off' : `it enters ${config.trigger}`;
    return dependency
      ? `No direct command: follows ${dependency.name} when ${action}.`
      : 'Choose the device whose power behavior this device follows.';
  }
  if (config.strategy === 'always_on') return 'Nothing to send: this device remains awake.';
  if (config.strategy === 'manual') return config.notes || 'An operator performs this action manually.';
  return 'No power command configured: leave this device unchanged.';
}

function DeviceLifecycleOnboarding({ areaId, topology, devices, onTopologyChange, selectedScreenId = null, showPowerPolicy = true }) {
  const screens = useMemo(() => (topology?.screens || []).filter((screen) => screen.id), [topology]);
  const [screenId, setScreenId] = useState('');
  const [focusedDeviceId, setFocusedDeviceId] = useState('');
  const [draft, setDraft] = useState({ version: 1, devices: {} });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [experiment, setExperiment] = useState({ phase: 'shutdown', methodId: '', targetDeviceId: '', effect: 'target_slept', notes: '', applyDependency: true });
  const [experimentResult, setExperimentResult] = useState(null);
  const activeScreenId = selectedScreenId || screenId;
  const screen = screens.find((entry) => Number(entry.id) === Number(activeScreenId)) || screens[0] || null;
  const screenDevices = useMemo(() => lifecycleDevices(screen, devices), [screen, devices]);
  const devicesById = useMemo(() => new Map(screenDevices.map((device) => [device.id, device])), [screenDevices]);
  const powerProfile = screen?.activationStrategy?.powerProfile || {};
  const methods = useMemo(() => [
    ...(powerProfile.startup?.methods || []).map((method) => ({ ...method, phase: 'startup' })),
    ...(powerProfile.shutdown?.methods || []).map((method) => ({ ...method, phase: 'shutdown' })),
  ], [powerProfile]);
  const methodsById = useMemo(() => new Map(methods.map((method) => [method.id, {
    ...method, label: powerMethodLabel(method.type, method.phase),
  }])), [methods]);

  useEffect(() => {
    if ((!screenId || selectedScreenId) && screen) {
      setScreenId(String(screen.id));
      setFocusedDeviceId(String(lifecycleDevices(screen, devices)[0]?.id || ''));
    }
  }, [screen, screenId, selectedScreenId, devices]);

  useEffect(() => {
    if (!screenDevices.some((device) => String(device.id) === String(focusedDeviceId))) {
      setFocusedDeviceId(String(screenDevices[0]?.id || ''));
    }
  }, [focusedDeviceId, screenDevices]);

  useEffect(() => {
    const saved = screen?.activationStrategy?.deviceLifecycleProfile;
    const next = { ...saved, version: 1, devices: {}, relationships: saved?.relationships || [] };
    screenDevices.forEach((device) => {
      const existing = saved?.devices?.[String(device.id)] || {};
      next.devices[String(device.id)] = {
        deviceId: device.id,
        recoveryAnchor: existing.recoveryAnchor === true,
        startup: { ...defaultPhase('startup'), ...(existing.startup || {}) },
        shutdown: { ...defaultPhase('shutdown'), ...(existing.shutdown || {}) },
      };
    });
    setDraft(next);
    setMessage('');
  }, [screen?.id, screen?.activationStrategy?.deviceLifecycleProfile, screenDevices]);

  const updateDevice = (deviceId, updater) => setDraft((current) => ({
    ...current,
    devices: {
      ...current.devices,
      [String(deviceId)]: updater(current.devices[String(deviceId)]),
    },
  }));

  const updatePhase = (deviceId, phase, field, value) => updateDevice(deviceId, (current) => ({
    ...current,
    [phase]: { ...current[phase], [field]: value },
  }));

  const setRecoveryAnchor = (deviceId, checked) => updateDevice(deviceId, (current) => ({
    ...current,
    recoveryAnchor: checked,
    shutdown: checked ? {
      ...current.shutdown,
      desiredState: 'remain_awake',
      strategy: 'always_on',
      methodId: null,
      dependsOnDeviceId: null,
    } : current.shutdown,
  }));

  const replaceProfile = (lifecycleProfile) => ({
    ...topology,
    screens: topology.screens.map((entry) => Number(entry.id) !== Number(screen.id) ? entry : ({
      ...entry,
      activationStrategy: { ...(entry.activationStrategy || {}), deviceLifecycleProfile: lifecycleProfile },
    })),
  });

  const save = async (profile = draft, successMessage = 'Device lifecycle strategy saved.') => {
    setBusy(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `/api/areas/${areaId}/topology/screens/${screen.id}/device-lifecycle-profile`,
        { lifecycleProfile: profile },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onTopologyChange?.(response.data.topology || replaceProfile(response.data.lifecycleProfile));
      setDraft(response.data.lifecycleProfile);
      setMessage(successMessage);
    } catch (error) {
      setMessage(error.response?.data?.message || error.message);
    } finally {
      setBusy(false);
    }
  };

  const experimentMethods = methods.filter((method) => method.phase === experiment.phase);
  const selectedExperimentMethod = experimentMethods.find((method) => method.id === experiment.methodId) || null;
  const causeDeviceId = Number(selectedExperimentMethod?.targetDeviceId || (
    selectedExperimentMethod?.targetRole === 'player' ? screen.preferredPlayerDeviceId : screen.televisionDeviceId
  ));

  const runCecExperiment = async () => {
    if (!selectedExperimentMethod || !experiment.targetDeviceId || causeDeviceId === Number(experiment.targetDeviceId)) return;
    let confirmCurrentState;
    if (selectedExperimentMethod.type === 'android_tv_power_toggle') {
      confirmCurrentState = experiment.phase === 'startup' ? 'off' : 'on';
      if (!window.confirm(`Confirm ${devicesById.get(causeDeviceId)?.name || 'the command device'} is ${confirmCurrentState.toUpperCase()}. Suitestream will send Power once.`)) return;
    }
    setBusy(true);
    setMessage('Running one bounded command. Watch only the selected observed device.');
    setExperimentResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/areas/${areaId}/topology/screens/${screen.id}/power-methods/test`,
        {
          phase: experiment.phase,
          methodId: selectedExperimentMethod.id,
          confirmCurrentState,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExperimentResult({ commandAccepted: response.data?.accepted === true });
      setMessage('Command finished. Record what happened to the observed device.');
    } catch (error) {
      setExperimentResult({ commandAccepted: false });
      setMessage(error.response?.data?.message || error.message);
    } finally {
      setBusy(false);
    }
  };

  const recordCecExperiment = async (observed) => {
    const relationship = {
      id: `cec-${experiment.phase}-${causeDeviceId}-${experiment.targetDeviceId}-${Date.now()}`,
      phase: experiment.phase,
      causeDeviceId,
      targetDeviceId: Number(experiment.targetDeviceId),
      methodId: experiment.methodId,
      effect: experiment.effect,
      status: observed ? 'confirmed' : 'failed',
      verifiedAt: new Date().toISOString(),
      evidence: {
        commandAccepted: experimentResult?.commandAccepted === true,
        operatorObserved: observed,
        notes: experiment.notes,
      },
    };
    const next = {
      ...draft,
      devices: { ...draft.devices },
      relationships: [...(draft.relationships || []), relationship],
    };
    if (observed && experiment.applyDependency) {
      const targetId = String(experiment.targetDeviceId);
      const trigger = experiment.effect === 'input_selected' ? 'input_selection'
        : experiment.effect === 'target_woke' || experiment.effect === 'tv_woke' ? 'wake' : 'standby';
      next.devices[targetId] = {
        ...next.devices[targetId],
        [experiment.phase]: {
          ...next.devices[targetId][experiment.phase],
          strategy: 'follows_device',
          dependsOnDeviceId: causeDeviceId,
          trigger,
          methodId: null,
        },
      };
    }
    await save(next, observed ? 'CEC relationship confirmed and saved.' : 'Failed CEC relationship recorded.');
    setExperimentResult(null);
  };

  const removeRelationship = async (id) => save({
    ...draft,
    relationships: (draft.relationships || []).filter((entry) => entry.id !== id),
  }, 'CEC relationship removed.');

  if (!screen) return null;
  return (
    <section className="device-lifecycle" aria-label="Device lifecycle onboarding">
      <div className="device-lifecycle__heading">
        <div><h3>Connected-device behaviour</h3><p>Review saved behaviour below. Change an override only when you have evidence; missing telemetry does not mean a device is off.</p></div>
        <label>{selectedScreenId ? `Device details · ${screen.name || 'Selected Screen'}` : 'Screen / device'}<select value={selectedScreenId ? focusedDeviceId : `${screen.id}:${focusedDeviceId}`} onChange={(event) => {
          if (selectedScreenId) {
            setFocusedDeviceId(event.target.value);
            return;
          }
          const [nextScreenId, nextDeviceId] = event.target.value.split(':');
          setScreenId(nextScreenId);
          setFocusedDeviceId(nextDeviceId);
        }}>{selectedScreenId
          ? screenDevices.map((device) => <option key={device.id} value={device.id}>{device.role} — {device.name}</option>)
          : screens.map((entry) => <optgroup key={entry.id} label={entry.name}>{lifecycleDevices(entry, devices).map((device) => <option key={`${entry.id}:${device.id}`} value={`${entry.id}:${device.id}`}>{device.role} — {device.name}</option>)}</optgroup>)}</select></label>
      </div>
      {selectedScreenId && <p>Showing devices connected to {screen.name || 'this Screen'}. To configure another TV, choose its TV in the main device dropdown at the top right.</p>}
      <div className="device-lifecycle__cards">
        {screenDevices.filter((device) => String(device.id) === String(focusedDeviceId)).map((device) => {
          const lifecycle = draft.devices[String(device.id)];
          if (!lifecycle) return null;
          return <article className="device-lifecycle__card" key={device.id}>
            <header><div><strong>{device.name}</strong><small>{device.role}</small></div><label><input type="checkbox" checked={lifecycle.recoveryAnchor} onChange={(event) => setRecoveryAnchor(device.id, event.target.checked)} /> Keep awake as recovery relay</label></header>
            {showPowerPolicy && device.powerPolicy?.items?.length > 0 && <details className="device-lifecycle__policy">
              <summary>Android power policy diagnostics</summary>
              <AndroidPowerPolicyDiagnostics areaId={areaId} screenId={screen.id} devices={[device]} />
            </details>}
            {['startup', 'shutdown'].map((phase) => {
              const config = lifecycle[phase];
              const directMethods = methods.filter((method) => {
                const fallbackId = method.targetRole === 'player' ? screen.preferredPlayerDeviceId : screen.televisionDeviceId;
                return method.phase === phase && Number(method.targetDeviceId || fallbackId) === device.id;
              });
              return <SetupPanel key={phase} title={phase === 'startup' ? 'Startup behaviour' : 'Shutdown behaviour'} description={lifecycleSummary(lifecycle, phase, devicesById, methodsById)}>
                <section className={`device-lifecycle__phase device-lifecycle__phase--${phase}`}>
                <label>Desired state<select value={config.desiredState} disabled={lifecycle.recoveryAnchor && phase === 'shutdown'} onChange={(event) => updatePhase(device.id, phase, 'desiredState', event.target.value)}>{(phase === 'startup' ? STARTUP_STATES : SHUTDOWN_STATES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <label>How it happens<select value={config.strategy} disabled={lifecycle.recoveryAnchor && phase === 'shutdown'} onChange={(event) => updatePhase(device.id, phase, 'strategy', event.target.value)}>{STRATEGIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                {config.strategy === 'direct_method' && <label>Control method<select value={config.methodId || ''} onChange={(event) => updatePhase(device.id, phase, 'methodId', event.target.value)}><option value="">Choose a saved method…</option>{directMethods.map((method) => <option key={method.id} value={method.id}>{methodsById.get(method.id)?.label}</option>)}</select>{!directMethods.length && <small>Add a method targeting this device in Power onboarding first.</small>}</label>}
                {config.strategy === 'follows_device' && <><label>Depends on<select value={config.dependsOnDeviceId || ''} onChange={(event) => updatePhase(device.id, phase, 'dependsOnDeviceId', Number(event.target.value) || null)}><option value="">Choose device…</option>{screenDevices.filter((entry) => entry.id !== device.id).map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></label><label>Trigger<select value={config.trigger} onChange={(event) => updatePhase(device.id, phase, 'trigger', event.target.value)}>{TRIGGERS[phase].map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label></>}
                {['manual', 'unavailable'].includes(config.strategy) && <label>Notes<input value={config.notes || ''} onChange={(event) => updatePhase(device.id, phase, 'notes', event.target.value)} placeholder="What must the operator know?" /></label>}
                <p>{lifecycleSummary(lifecycle, phase, devicesById, methodsById)}</p>
                </section>
              </SetupPanel>;
            })}
          </article>;
        })}
      </div>
      <SetupPanel title="Manual relationship experiment" description="Advanced: send one command and record an observed effect on another device">
      <section className="device-lifecycle__cec">
        <h4>Test a directional HDMI-CEC relationship</h4>
        <p>Send one saved command to one device, then record the effect on a different device. Results apply only to this exact pair and direction.</p>
        <div className="device-lifecycle__cec-form">
          <label>Phase<select value={experiment.phase} onChange={(event) => setExperiment({ ...experiment, phase: event.target.value, methodId: '', effect: event.target.value === 'startup' ? 'target_woke' : 'target_slept' })}><option value="startup">Startup</option><option value="shutdown">Shutdown</option></select></label>
          <label>Command to send<select value={experiment.methodId} onChange={(event) => setExperiment({ ...experiment, methodId: event.target.value })}><option value="">Choose saved method…</option>{experimentMethods.map((method) => {
            const targetId = Number(method.targetDeviceId || (method.targetRole === 'player' ? screen.preferredPlayerDeviceId : screen.televisionDeviceId));
            return <option key={method.id} value={method.id}>{methodsById.get(method.id)?.label} → {devicesById.get(targetId)?.name || `Device ${targetId}`}</option>;
          })}</select></label>
          <label>Observe device<select value={experiment.targetDeviceId} onChange={(event) => setExperiment({ ...experiment, targetDeviceId: event.target.value })}><option value="">Choose other device…</option>{screenDevices.filter((device) => device.id !== causeDeviceId).map((device) => <option key={device.id} value={device.id}>{device.name}</option>)}</select></label>
          <label>Expected effect<select value={experiment.effect} onChange={(event) => setExperiment({ ...experiment, effect: event.target.value })}>{CEC_EFFECTS[experiment.phase].map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Observation notes<input value={experiment.notes} onChange={(event) => setExperiment({ ...experiment, notes: event.target.value })} placeholder="LED, picture, telemetry, or remote response" /></label>
          <label className="device-lifecycle__cec-check"><input type="checkbox" checked={experiment.applyDependency} onChange={(event) => setExperiment({ ...experiment, applyDependency: event.target.checked })} /> If confirmed, configure the observed device to follow the command device</label>
          <button type="button" disabled={busy || !selectedExperimentMethod || !experiment.targetDeviceId || causeDeviceId === Number(experiment.targetDeviceId)} onClick={runCecExperiment}>Run one test command</button>
        </div>
        {experimentResult && <div className="device-lifecycle__cec-result"><strong>Did the expected effect happen?</strong><span>Command accepted: {experimentResult.commandAccepted ? 'yes' : 'no / unknown'}</span><button type="button" disabled={busy} onClick={() => recordCecExperiment(true)}>Yes — confirm relationship</button><button type="button" disabled={busy} onClick={() => recordCecExperiment(false)}>No — record failed test</button></div>}
        <div className="device-lifecycle__relationships">
          {(draft.relationships || []).map((relationship) => <div key={relationship.id}><span><strong>{devicesById.get(relationship.causeDeviceId)?.name}</strong> → <strong>{devicesById.get(relationship.targetDeviceId)?.name}</strong><small>{relationship.phase} · {String(relationship.effect).replaceAll('_', ' ')} · {relationship.status}</small></span><button type="button" disabled={busy} onClick={() => removeRelationship(relationship.id)}>Remove</button></div>)}
          {!(draft.relationships || []).length && <p>No pair-specific CEC relationships recorded yet.</p>}
        </div>
      </section>
      </SetupPanel>
      <div className="device-lifecycle__actions"><button type="button" disabled={busy} onClick={() => save()}>Save device lifecycle</button>{message && <p role="status">{message}</p>}</div>
    </section>
  );
}

export default DeviceLifecycleOnboarding;
